export const FISCAL_FAMILY_IDS = [
    "NCM",
    "CEST",
    "IPI",
    "PIS_COFINS",
    "ICMS",
    "ICMS_ST",
    "MVA",
    "ISS",
];
function asRecord(value) {
    return value !== null && typeof value === "object" && !Array.isArray(value)
        ? value
        : {};
}
function text(value) {
    return typeof value === "string" && value.trim().length > 0 ? value.trim() : undefined;
}
function isTaxed(component) {
    const tax = asRecord(component);
    return tax.treatment === "TAXED" && text(tax.rate) !== undefined;
}
/** Reads one fiscal rule from a published snapshot without trusting extra fields. */
export function snapshotFromUnknownRule(rule) {
    const record = asRecord(rule);
    const match = asRecord(record.match);
    const taxes = asRecord(record.taxes);
    const icms = asRecord(taxes.icms);
    return {
        ncm: text(match.ncm) ?? null,
        cest: text(match.cest) ?? null,
        ipiTaxed: isTaxed(taxes.ipi),
        pisTaxed: isTaxed(taxes.pis),
        cofinsTaxed: isTaxed(taxes.cofins),
        issTaxed: isTaxed(taxes.iss),
        hasIcmsRate: text(icms.destinationInternalRate) !== undefined || text(icms.interstateRate) !== undefined,
        isIcmsSt: icms.mode === "ST",
        hasMva: text(icms.originalMva) !== undefined || text(icms.adjustedMva) !== undefined,
    };
}
export function countPublishedFamilies(rules) {
    const ncms = new Set();
    const counts = {
        NCM: 0,
        CEST: 0,
        IPI: 0,
        PIS_COFINS: 0,
        ICMS: 0,
        ICMS_ST: 0,
        MVA: 0,
        ISS: 0,
    };
    for (const rule of rules) {
        if (rule.ncm)
            ncms.add(rule.ncm);
        if (rule.cest)
            counts.CEST += 1;
        if (rule.ipiTaxed)
            counts.IPI += 1;
        if (rule.pisTaxed || rule.cofinsTaxed)
            counts.PIS_COFINS += 1;
        if (rule.hasIcmsRate)
            counts.ICMS += 1;
        if (rule.isIcmsSt)
            counts.ICMS_ST += 1;
        if (rule.hasMva)
            counts.MVA += 1;
        if (rule.issTaxed)
            counts.ISS += 1;
    }
    counts.NCM = ncms.size;
    return counts;
}
function emptyPublished() {
    return {
        NCM: 0,
        CEST: 0,
        IPI: 0,
        PIS_COFINS: 0,
        ICMS: 0,
        ICMS_ST: 0,
        MVA: 0,
        ISS: 0,
    };
}
const DRAFTABLE_FAMILIES = new Set(["CEST", "IPI", "ICMS", "ICMS_ST", "MVA"]);
/** Goods/wholesale list pricing does not govern these families in this base. */
export const OUTSIDE_BASE_FAMILY_IDS = ["PIS_COFINS", "ISS"];
const OUTSIDE_BASE_FAMILIES = new Set(OUTSIDE_BASE_FAMILY_IDS);
/**
 * Municipal ISS is not a Backchannel wholesale family. Keep the id for snapshot
 * counts; omit the operator ledger row. Federal goods (IPI) and state ICMS stay.
 */
export const HIDDEN_LEDGER_FAMILY_IDS = ["ISS"];
const HIDDEN_LEDGER_FAMILIES = new Set(HIDDEN_LEDGER_FAMILY_IDS);
function resolve(family, published, pending, reference) {
    const catalogCount = reference > 0 ? reference : null;
    // CEST catalog is the inventory; published CEST-on-rules are not the universe.
    if (family === "CEST" && reference > 0) {
        return {
            family,
            state: published > 0 ? "published" : "reference",
            count: reference,
            catalogCount,
        };
    }
    if (published > 0)
        return { family, state: "published", count: published, catalogCount };
    if (OUTSIDE_BASE_FAMILIES.has(family)) {
        return { family, state: "not_collected", count: 0, catalogCount: null };
    }
    if (pending > 0 && DRAFTABLE_FAMILIES.has(family)) {
        return { family, state: "draft", count: pending, catalogCount };
    }
    if (reference > 0)
        return { family, state: "reference", count: reference, catalogCount };
    return { family, state: "absent", count: 0, catalogCount };
}
/**
 * One row per fiscal family, in the order a list price is assembled.
 * Published counts come from the active ruleset snapshot. Draft counts come
 * from live PENDING_REVIEW fiscal proposals, never from an import receipt.
 * Reference counts are official catalogs that do not yet govern a price.
 */
export function buildFiscalLedger(input) {
    const published = input.published ?? emptyPublished();
    const pending = input.pendingFiscalProposals ?? 0;
    const reference = input.reference ?? {};
    return FISCAL_FAMILY_IDS.filter((family) => !HIDDEN_LEDGER_FAMILIES.has(family)).map((family) => resolve(family, published[family], pending, reference[family] ?? 0));
}
//# sourceMappingURL=fiscal-ledger.js.map